<script setup lang="ts">
import { ref } from 'vue'
import { ccmCertificateManagementApi, organizationManagementApi } from '@/api'
import Modal from '@/components/modals/Modal.vue'
import useModal from '@/composables/useModal'
import { addToast } from '@/composables/useToasts'
import { useI18n } from 'vue-i18n'
import Loading from '@/components/loading/Loading.vue'
import { CertificatePushStatusPayloadCertificateStatusEnum, CertificateRequestStatus } from '@coac-gmbh/saifty-ccm-api'
import { CertificateNotificationData, CertificateRejectionType } from '@/composables/useDataSpace'
import MultipleFreeTexts from '@/components/forms/MultipleFreeTexts.vue'

interface Props {
  certificateRejectionType: CertificateRejectionType
  certificateData: CertificateNotificationData
}

const props = defineProps<Props>()
const emits = defineEmits(['certificateProccesed'])

const {
  modal: handleCertificateRejectModal,
  openModal: openHandleCertificateRejectModal,
  hideModal: hideHandleCertificateRejectModal,
} = useModal()
const { t } = useI18n()
const certificateControls = ref<{ type?: CertificateRejectionType; rejecting?: boolean; reasons: string[] }>({
  reasons: [],
})

async function rejectCertificateRequest() {
  certificateControls.value.rejecting = true
  try {
    const orgResponse = await organizationManagementApi.getAllOrganization({ filterBy: 'all' })
    const senderFeedbackUrl = orgResponse.data[0]?.connectorDSPEndpoint || ''

    const body = {
      id: props.certificateData.id || '',
      counterPartyAddress: props.certificateData.header?.senderFeedbackUrl || '',
      certificateStatusPayload: {
        senderFeedbackUrl,
        certificateErrors: certificateControls.value.reasons.map((reason) => ({ message: reason })),
      },
    }

    if (props.certificateRejectionType === CertificateRejectionType.PULL) {
      await ccmCertificateManagementApi.certificatePullStatusChange({
        ...body,
        certificateStatusPayload: {
          ...body.certificateStatusPayload,
          requestStatus: CertificateRequestStatus.Rejected,
        },
      })
    } else {
      await ccmCertificateManagementApi.certificatePushStatusChange({
        ...body,
        certificatePushStatusPayload: {
          ...body.certificateStatusPayload,
          certificateStatus: CertificatePushStatusPayloadCertificateStatusEnum.Rejected,
        },
      })
    }

    addToast({
      text: t('Certificate request rejected successfully'),
      variant: 'success',
    })
    certificateControls.value.reasons = []
    hideHandleCertificateRejectModal()
    emits('certificateProccesed')
  } catch (error) {
    console.error(error)
    addToast({
      text: t('Error rejecting certificate request'),
      variant: 'danger',
    })
  } finally {
    certificateControls.value.rejecting = false
  }
}

defineExpose({
  openModal: openHandleCertificateRejectModal,
})
</script>

<template>
  <Modal
    id="handle-certificate-reject-modal"
    ref="handleCertificateRejectModal"
    body-classes="p-4 m-3"
    certificate-controls
    size="lg"
    border-classes="rounded-15"
    :title="t('Reject Certificate')"
    static
    class="text-dark"
    new-style
    @click.stop.prevent
  >
    <div v-if="props.certificateData.content?.certificateType" class="d-flex mt-3">
      <label class="text-primary-800 me-1">{{ t('Certificate Type') }}:</label>
      <input
        type="text"
        class="fw-bold bg-transparent"
        :value="props.certificateData.content?.certificateType || ''"
        placeholder="--"
        disabled
      />
    </div>

    <MultipleFreeTexts v-model="certificateControls.reasons" class="mt-2" :label="t('Rejection Reasons')" />

    <div class="d-flex gap-3 mt-4 justify-content-end">
      <button
        class="btn btn-secondary"
        :disabled="certificateControls.rejecting"
        @click="hideHandleCertificateRejectModal"
      >
        {{ t('Cancel') }}
      </button>

      <button
        class="btn btn-primary d-flex gap-2 align-items-center"
        :disabled="certificateControls.rejecting"
        @click="rejectCertificateRequest"
      >
        {{ 'Reject' }}
        <Loading v-if="certificateControls.rejecting" extra-classes="m-0" size="sm" />
      </button>
    </div>
  </Modal>
</template>
